import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Email,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const PublicFooter: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'Features', path: '/features' },
        { label: 'Pricing', path: '/pricing' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', path: '/about' },
        { label: 'Our Story', path: '/about#story' },
        { label: 'Contact', path: '/help' },
        { label: 'Support', path: '/help' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Help Center', path: '/help' },
        { label: 'Cost Calculator', path: '/compare' },
        { label: 'Get Started', path: '/login' },
        { label: 'FAQ', path: '/pricing#faq' },
        { label: 'System Status', path: '/status' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', path: '/legal/terms' },
        { label: 'Privacy Policy', path: '/legal/privacy' },
        { label: 'Cookie Policy', path: '/legal/cookies' },
        { label: 'Acceptable Use', path: '/legal/acceptable-use' },
        { label: 'Billing Terms', path: '/legal/billing' },
        { label: 'DPA', path: '/legal/dpa' },
      ],
    },
  ];


  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
        mt: 'auto',
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid item xs={12} md={3}>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src="/assets/tier-one-content_icon_dynamic-layers_128.png"
                  alt="Tier One Content Logo"
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 0.5,
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Tier One Content
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Simple, powerful content sharing for teams. Organize with flexible tags, search across all attributes, and save 80-90% with concurrent user pricing.
              </Typography>
            </Box>
          </Grid>

          {/* Links Columns */}
          {footerSections.map((section) => (
            <Grid item xs={6} sm={4} md={2.25} key={section.title}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary',
                }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.links.map((link) => (
                  <MuiLink
                    key={link.label}
                    component={Link}
                    to={link.path}
                    underline="none"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    {link.label}
                  </MuiLink>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mt: 6, mb: 3 }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {currentYear} Jakatech LLC. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
            }}
          >
            <MuiLink
              component={Link}
              to="/legal/terms"
              underline="none"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              Terms
            </MuiLink>
            <MuiLink
              component={Link}
              to="/legal/privacy"
              underline="none"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              Privacy
            </MuiLink>
            <MuiLink
              component={Link}
              to="/legal/cookies"
              underline="none"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              Cookies
            </MuiLink>
            <MuiLink
              href="mailto:support@tieronecontent.com"
              underline="none"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Email sx={{ fontSize: 16 }} />
              Contact
            </MuiLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicFooter;